import { Spacing } from '@persona-id/design-tokens';
import { ArrowDownIcon, ArrowUpIcon, MinusIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { bottom } from '@popperjs/core';
import { DateTime, Duration, Settings } from 'luxon';
import { FC, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { LoadingShimmerParagraph } from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import Tooltip from 'dashboard/components/Tooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { MetricsInquiryVolume } from 'dashboard/models/metrics';
import { Heading, Paragraph } from 'design-system/components/Text';
import {
  ABBR_DATE_FORMAT,
  HOURS_ELAPSED_FORMAT,
  MINUTES_SECONDS_ELAPSED_FORMAT,
} from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';

interface DeltaDomain {
  domain_start?: Iso8601DateString;
  domain_end?: Iso8601DateString;
}

interface MetricValue {
  value: number;
  displayValue: string;
}

type DeltaType = 'relative-percentage' | 'absolute-percentage' | 'duration';

interface HighlightProps {
  label: string;
  toolTipCopy: string;
  metricValue: MetricValue;
  previousPeriodMetricValue?: MetricValue;
  deltaType?: DeltaType;
  minWidth?: number;
  previousPeriodDomain?: DeltaDomain;
  isInquiryVolumeDeltaEnabled: boolean;
  invertColors?: boolean;
  isLoading: boolean;
}

const MetricsCard = styled('div')<{ minWidth: number }>`
  padding: ${Spacing[4]};
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  min-width: ${({ minWidth }) => `${minWidth}px`};
`;

const MetricsCardRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: end;
`;

const DeltaContainer = styled('div')<{
  deltaValue?: number;
  invertColors: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;

  p,
  svg {
    color: ${({ deltaValue, invertColors, theme }) => {
      if (deltaValue == null || deltaValue === 0) {
        return null;
      }
      if (deltaValue < 0) {
        if (invertColors) {
          return theme.color.variants.success.default;
        }
        return theme.color.variants.danger.default;
      } else {
        if (invertColors) {
          return theme.color.variants.danger.default;
        }
        return theme.color.variants.success.default;
      }
    }};
  }
`;

const ToolTipContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${Spacing[2]};
  gap: 2px;
  width: 220px;
`;

const formatPercentage = (
  numerator: number,
  denominator: number,
): { displayValue: string; value: number } => {
  if (denominator === 0) {
    return {
      displayValue: 'N/A',
      value: 0,
    };
  }
  return {
    displayValue: `${formatNumber(
      // we take the absolute value of the percentage to ensure that we don't show a negative sign
      // because we are showing an icon to indicate the direction of the change
      Math.abs((numerator * 100) / denominator),
    )}%`,
    value: (numerator * 100) / denominator,
  };
};

const DEFAULT_VOLUME = {
  created: 0,
  started: 0,
  started_with_interaction: 0,
  finished: 0,
  succeeded_finished: 0,
  rejected_finished: 0,
  medianCompletedTime: 0,
};

const formatDurationFormatted = (medianCompletion?: number) => {
  if (medianCompletion == null) {
    return null;
  }
  // we have a fancy negative icon we use so we take absolute here to prevent adding another negative sign
  const absMedianCompletion = Math.abs(medianCompletion);
  const duration = Duration.fromObject({ seconds: absMedianCompletion });
  if (absMedianCompletion > 60 * 60) {
    return duration.toFormat(HOURS_ELAPSED_FORMAT);
  }
  return duration.toFormat(MINUTES_SECONDS_ELAPSED_FORMAT);
};

export const formatInquiryVolumeMetrics = (
  volume: MetricsInquiryVolume,
  medianCompletion: number,
) => {
  // merge volume with default volume to ensure that we have all the keys
  const mergedVolume = { ...DEFAULT_VOLUME, ...volume };

  return {
    created: {
      displayValue: formatNumber(mergedVolume['created']),
      value: mergedVolume['created'],
    },
    started: formatPercentage(mergedVolume['started'], mergedVolume['created']),
    interacted: formatPercentage(mergedVolume['started_with_interaction'], mergedVolume['started']),
    finished: formatPercentage(mergedVolume['finished_started'], mergedVolume['started']),
    success: formatPercentage(mergedVolume['succeeded_finished'], mergedVolume['finished']),
    rejected: formatPercentage(mergedVolume['rejected_finished'], mergedVolume['finished']),
    medianCompletedTime: {
      displayValue: medianCompletion == null ? 'N/A' : formatDurationFormatted(medianCompletion),
      value: medianCompletion,
    },
  };
};

const calculateDelta = (
  value: number,
  previousPeriodMetricValues: MetricValue,
  deltaType: DeltaType,
) => {
  const calculatedDelta =
    previousPeriodMetricValues.value == null ? null : value - previousPeriodMetricValues.value;
  switch (deltaType) {
    case 'absolute-percentage':
      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delta: formatPercentage(calculatedDelta, 100),
        popOverText: 'Absolute change compared to',
      };
    case 'relative-percentage':
      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delta: formatPercentage(calculatedDelta, previousPeriodMetricValues.value),
        popOverText: 'Relative change compared to',
      };
    case 'duration':
      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delta: { displayValue: formatDurationFormatted(calculatedDelta), value: calculatedDelta },
        popOverText: 'Absolute change compared to',
      };
  }
};

const deltaIcon = (
  delta: { displayValue: string; value?: number },
  deltaType: DeltaType,
  fontSize: string,
) => {
  if (delta.value == null || delta.value === 0) {
    return null;
  }
  switch (deltaType) {
    case 'absolute-percentage':
    case 'duration':
      if (delta.value < 0) {
        return <MinusIcon width={fontSize} height={fontSize} />;
      } else {
        return <PlusIcon width={fontSize} height={fontSize} />;
      }
    case 'relative-percentage':
      if (delta.value < 0) {
        return <ArrowDownIcon width={fontSize} height={fontSize} />;
      } else {
        return <ArrowUpIcon width={fontSize} height={fontSize} />;
      }
  }
};

const HighlightCard: FC<HighlightProps> = ({
  label,
  metricValue: { value, displayValue },
  toolTipCopy,
  previousPeriodMetricValue,
  minWidth = 90,
  deltaType = 'absolute-percentage',
  isInquiryVolumeDeltaEnabled,
  previousPeriodDomain,
  invertColors = false,
  isLoading,
}) => {
  const { text } = useContext(ThemeContext);
  const displayDelta = (isLoading: boolean) => {
    if (isLoading) {
      return (
        <DeltaContainer invertColors={invertColors}>
          <LoadingShimmerParagraph size='sm' width='25%' />
        </DeltaContainer>
      );
    }
    // if there is no previous period, don't display the delta
    if (!isInquiryVolumeDeltaEnabled) return null;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const delta = calculateDelta(value, previousPeriodMetricValue, deltaType);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const formattedDomainStart = previousPeriodDomain.domain_start
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        DateTime.fromISO(previousPeriodDomain.domain_start, {
          zone: Settings.defaultZone,
        }).toFormat(ABBR_DATE_FORMAT)
      : DateTime.local().toFormat(ABBR_DATE_FORMAT);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const formattedDomainEnd = previousPeriodDomain.domain_end
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        DateTime.fromISO(previousPeriodDomain.domain_end, { zone: Settings.defaultZone })
          .minus({ days: 1 })
          .toFormat(ABBR_DATE_FORMAT)
      : DateTime.local().minus({ days: 1 }).toFormat(ABBR_DATE_FORMAT);
    // calculate display values for the delta
    return (
      <Tooltip
        placement={bottom}
        trigger='hover'
        hideArrow={true}
        tooltip={
          <ToolTipContainer>
            <Paragraph>{delta.popOverText}</Paragraph>
            <Paragraph>
              {formattedDomainStart} — {formattedDomainEnd}
            </Paragraph>
            <Heading size='md' color='primary' as='p'>
              {!isLoading &&
                previousPeriodMetricValue != null &&
                previousPeriodMetricValue.displayValue != null &&
                previousPeriodMetricValue.displayValue}
            </Heading>
          </ToolTipContainer>
        }
      >
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <DeltaContainer deltaValue={delta.delta.value} invertColors={invertColors}>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {deltaIcon(delta.delta, deltaType, text.paragraph.xs.fontSize)}
          <Paragraph size='sm'>{delta.delta.displayValue}</Paragraph>
        </DeltaContainer>
      </Tooltip>
    );
  };

  return (
    <MetricsCard minWidth={minWidth}>
      <MetricsCardRow>
        <Paragraph>{label}</Paragraph>
        <HelpIcon>{toolTipCopy}</HelpIcon>
      </MetricsCardRow>
      <Heading size='md' color='primary' as='p'>
        {isLoading ? <LoadingShimmerParagraph size='md' /> : displayValue}
      </Heading>
      {displayDelta(isLoading)}
    </MetricsCard>
  );
};
export default HighlightCard;
